import { noop } from 'lodash';
import { memo, useCallback, useMemo } from 'react';

import { useTrackSwitchedSortType } from '@air/analytics';
import { type SortDirection } from '@air/api/types';
import { CheckIcon, EllipsisIcon } from '@air/design-icons';
import {
  DropdownMenu,
  type DropdownMenuOption,
  type DropdownMenuProps,
  renderDropdownItems,
} from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { useWorkspaceSidebarPushLock } from '~/components/v2/hooks/useWorkspaceSidebarPushLock';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSubnavSort } from '~/swr-hooks/subnav/useSubnavSort';
import { useSubnavSortOptions } from '~/swr-hooks/subnav/useSubnavSortOptions';
import { useUpdateSubnavSort } from '~/swr-hooks/subnav/useUpdateSubnavSort';
import { canChangeWorkspaceDetails } from '~/utils/permissions/workspacePermissions';

const SIDEBAR_BOARDS_MORE_LOCK_TOKEN = 'sidebar-boards-more';

export type WorkspaceSidebarWorkspaceBoardsMoreDropdownProps = Pick<DropdownMenuProps, 'onOpenChange'>;

export const WorkspaceSidebarWorkspaceBoardsMoreDropdown = memo(
  ({ onOpenChange }: WorkspaceSidebarWorkspaceBoardsMoreDropdownProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { data: currentSubnavSort } = useSubnavSort();
    const { mutate: updateSubnavSort } = useUpdateSubnavSort();
    const { data: sortOptionsData } = useSubnavSortOptions();
    const { trackSwitchedSortType } = useTrackSwitchedSortType();
    const { data: permissions } = useCurrentWorkspacePermissionsContext();
    const canChangeSubnavOption = canChangeWorkspaceDetails(permissions);

    const handleChangeSubnavOption = useCallback(
      (name: string, direction: SortDirection) => {
        updateSubnavSort({ name, direction });
        trackSwitchedSortType({ sortingFieldName: name, direction, location: 'sub navigation' });
      },
      [trackSwitchedSortType, updateSubnavSort],
    );

    const options = useMemo(() => {
      const optionsArray: DropdownMenuOption[] = [
        {
          id: 'sort-boards-by',
          label: 'Sort boards by...',
          type: 'title',
        },
      ];

      sortOptionsData?.forEach(({ displayName, name, direction }) => {
        const isActiveOption =
          name === currentSubnavSort?.boardSort?.name && direction === currentSubnavSort?.boardSort.direction;

        optionsArray.push({
          id: `${name}-${direction}`,
          label: displayName,
          onSelect: !isActiveOption ? () => handleChangeSubnavOption(name, direction) : noop,
          suffix: isActiveOption ? <CheckIcon size="sm" /> : null,
          type: 'item',
        });
      });

      return optionsArray;
    }, [
      currentSubnavSort?.boardSort.direction,
      currentSubnavSort?.boardSort?.name,
      handleChangeSubnavOption,
      sortOptionsData,
    ]);

    const { handleOpenChange: handleLockOpenChange } = useWorkspaceSidebarPushLock({
      token: SIDEBAR_BOARDS_MORE_LOCK_TOKEN,
    });

    const handleOpenChange = useCallback(
      (open: boolean) => {
        handleLockOpenChange(open);
        onOpenChange?.(open);
      },
      [handleLockOpenChange, onOpenChange],
    );

    if (!currentWorkspace || !sortOptionsData || !canChangeSubnavOption) {
      return null;
    }

    return (
      <DropdownMenu
        onOpenChange={handleOpenChange}
        trigger={
          <IconButton
            adsIcon
            appearance="ghost"
            color="gray"
            className="pointer-events-none ui2026:pointer-events-auto opacity-0 ui2026:opacity-100 group-hover/workspace-sidebar-workspace-boards-header:pointer-events-auto group-hover/workspace-sidebar-workspace-boards-header:opacity-100"
            data-testid="WORKSPACE_SIDEBAR_WORKSPACE_BOARDS_MORE_DROPDOWN_BUTTON"
            icon={EllipsisIcon}
            label="Show list options"
            size="small"
          />
        }
        data-testid="WORKSPACE_SIDEBAR_WORKSPACE_BOARDS_MORE_DROPDOWN"
      >
        {renderDropdownItems({ options })}
      </DropdownMenu>
    );
  },
);

WorkspaceSidebarWorkspaceBoardsMoreDropdown.displayName = 'WorkspaceSidebarWorkspaceBoardsMoreDropdown';
